
import { useState } from 'react';

interface FAQItem {
  question: string;
  answer: string;
  category: 'residential' | 'hospital' | 'billing' | 'scheduling';
}

export default function FAQSection() {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  const faqs: FAQItem[] = [
    {
      question: '¿Qué servicios están incluidos en el cuidado residencial?',
      answer: 'Nuestros servicios de cuidado residencial incluyen asistencia personalizada de cuidado en casa, cuidado de enfermería profesional, apoyo en actividades diarias, servicios de compañía, apoyo de movilidad y rehabilitación, y supervisión y control de medicamentos. Cada plan de cuidado se personaliza para satisfacer las necesidades específicas del paciente.',
      category: 'residential'
    },
    {
      question: '¿Qué tan rápido pueden comenzar los servicios de cuidado?',
      answer: 'Típicamente podemos comenzar los servicios de cuidado dentro de 24-48 horas después de completar el proceso de admisión del paciente. Para situaciones urgentes, ofrecemos colocación acelerada y a menudo podemos comenzar el cuidado el mismo día.',
      category: 'scheduling'
    },
    {
      question: '¿Sus cuidadores están certificados y asegurados?',
      answer: 'Sí, todos nuestros cuidadores son profesionales de salud totalmente certificados con licencias y credenciales apropiadas. También están completamente asegurados y afianzados. Realizamos verificaciones exhaustivas de antecedentes y proporcionamos capacitación continua para garantizar la más alta calidad de cuidado.',
      category: 'residential'
    },
    {
      question: '¿Cuál es la diferencia entre asistencia hospitalaria pública y privada?',
      answer: 'La asistencia hospitalaria pública proporciona acompañamiento profesional y apoyo en instalaciones de salud públicas, incluyendo coordinación con personal médico y navegación de sistemas de salud públicos. Los servicios de centros privados ofrecen coordinación de cuidado premium mejorada en instalaciones médicas privadas con profesionales de cuidado dedicados y servicios de comodidad adicionales.',
      category: 'hospital'
    },
    {
      question: '¿Aceptan seguro?',
      answer: 'Sí, trabajamos con la mayoría de los principales proveedores de seguros. Podemos ayudarle a entender su cobertura y beneficios. Nuestro equipo de facturación trabajará directamente con su compañía de seguros para maximizar sus beneficios y minimizar los costos de bolsillo.',
      category: 'billing'
    },
    {
      question: '¿Puedo cambiar o cancelar una cita programada?',
      answer: 'Sí, puede modificar o cancelar citas con al menos 24 horas de anticipación. Por favor contáctenos lo antes posible si necesita hacer cambios. Entendemos que las necesidades de salud pueden cambiar, y nos esforzamos por ser lo más flexibles posible.',
      category: 'scheduling'
    },
    {
      question: '¿Qué áreas atienden?',
      answer: 'Proporcionamos servicios en toda la región. Contáctenos para confirmar la disponibilidad del servicio en su área específica. Estamos expandiendo continuamente nuestras áreas de servicio para llegar a más pacientes que necesitan cuidado de calidad.',
      category: 'residential'
    },
    {
      question: '¿Cómo aseguran la calidad del cuidado?',
      answer: 'Mantenemos altos estándares de calidad a través de supervisión regular, capacitación continua de cuidadores, sistemas de retroalimentación de pacientes y cumplimiento de todas las regulaciones de salud. Nuestros coordinadores de cuidado realizan chequeos regulares para garantizar la satisfacción del paciente y ajustar los planes de cuidado según sea necesario.',
      category: 'residential'
    },
    {
      question: '¿Cuáles son sus tarifas para servicios de asistencia hospitalaria?',
      answer: 'Nuestras tarifas varían dependiendo del tipo de servicio, duración y necesidades específicas. Ofrecemos precios competitivos tanto para asistencia hospitalaria pública como privada. Contáctenos para una cotización detallada basada en sus requisitos específicos.',
      category: 'billing'
    },
    {
      question: '¿Está disponible el cuidado 24/7?',
      answer: 'Sí, ofrecemos servicios de cuidado 24/7 para pacientes que requieren asistencia las 24 horas. Nuestro equipo está disponible a todas horas para proporcionar apoyo continuo y garantizar la seguridad y comodidad del paciente.',
      category: 'scheduling'
    },
    {
      question: '¿Cómo empiezo con los servicios de URI?',
      answer: '¡Comenzar es fácil! Complete nuestro formulario de admisión de pacientes en esta página, o llámenos directamente. Programaremos una consulta inicial para evaluar las necesidades, crear un plan de cuidado personalizado y emparejarlo con el cuidador adecuado.',
      category: 'residential'
    },
    {
      question: '¿Pueden los miembros de la familia participar en el proceso de cuidado?',
      answer: '¡Absolutamente! Alentamos la participación familiar y mantenemos comunicación abierta con los miembros de la familia. Proporcionamos actualizaciones regulares y damos la bienvenida a la participación familiar en la planificación del cuidado y la toma de decisiones.',
      category: 'residential'
    }
  ];

  const categories = [
    { id: 'all', name: 'Todas las Preguntas', icon: 'ri-question-line', color: 'gray' },
    { id: 'residential', name: 'Cuidado Residencial', icon: 'ri-home-heart-line', color: 'uri-green' },
    { id: 'hospital', name: 'Servicios Hospitalarios', icon: 'ri-hospital-line', color: 'uri-blue' },
    { id: 'billing', name: 'Facturación y Seguro', icon: 'ri-money-dollar-circle-line', color: 'uri-teal' },
    { id: 'scheduling', name: 'Programación', icon: 'ri-calendar-check-line', color: 'uri-green-light' }
  ];

  const filteredFAQs = selectedCategory === 'all' 
    ? faqs 
    : faqs.filter(faq => faq.category === selectedCategory);

  const getCategoryColor = (category: string) => {
    const colors: { [key: string]: string } = {
      residential: 'text-uri-green',
      hospital: 'text-uri-blue',
      billing: 'text-uri-teal',
      scheduling: 'text-uri-green-light'
    };
    return colors[category] || 'text-gray-600';
  };

  return (
    <section id="faq" className="py-20 bg-gradient-to-br from-uri-blue/5 to-uri-teal/5">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-12">
          <h3 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            Preguntas Frecuentes
          </h3>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Encuentre respuestas a preguntas comunes sobre nuestros servicios de salud, o chatee con nuestro asistente para ayuda personalizada
          </p>
        </div>

        <div className="grid lg:grid-cols-11 gap-8">
          {/* Left Side - FAQ Accordion */}
          <div className="lg:col-span-6">
            {/* Category Filter */}
            <div className="flex flex-wrap gap-2 mb-6">
              {categories.map(category => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                    selectedCategory === category.id
                      ? 'bg-uri-teal text-white shadow-md'
                      : 'bg-white text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <i className={`${category.icon} mr-1`}></i>
                  {category.name}
                </button>
              ))}
            </div>

            {/* FAQ Items */}
            <div className="space-y-3">
              {filteredFAQs.map((faq, index) => (
                <div
                  key={index}
                  className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-md transition-shadow"
                >
                  <button
                    onClick={() => setExpandedIndex(expandedIndex === index ? null : index)}
                    className="w-full flex items-start justify-between p-5 text-left cursor-pointer hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex-1 pr-4">
                      <div className="flex items-center gap-2 mb-1">
                        <i className={`${getCategoryColor(faq.category)} text-lg`}>
                          {faq.category === 'residential' && <i className="ri-home-heart-line"></i>}
                          {faq.category === 'hospital' && <i className="ri-hospital-line"></i>}
                          {faq.category === 'billing' && <i className="ri-money-dollar-circle-line"></i>}
                          {faq.category === 'scheduling' && <i className="ri-calendar-check-line"></i>}
                        </i>
                      </div>
                      <h4 className="font-semibold text-gray-900 text-base">{faq.question}</h4>
                    </div>
                    <i className={`ri-arrow-${expandedIndex === index ? 'up' : 'down'}-s-line text-xl text-gray-600 flex-shrink-0`}></i>
                  </button>
                  
                  {expandedIndex === index && (
                    <div className="px-5 pb-5">
                      <p className="text-sm text-gray-700 leading-relaxed">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="mt-8 text-center">
              <p className="text-gray-600 mb-4">¿Todavía tiene preguntas?</p>
              <button
                onClick={() => document.querySelector('#vapi-widget-floating-button')?.dispatchEvent(new Event('click'))}
                className="px-6 py-3 bg-uri-teal text-white font-semibold rounded-lg hover:bg-uri-teal-dark transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-chat-3-line mr-2"></i>
                Chatee con Nuestro Asistente de Cuidado
              </button>
            </div>
          </div>

          {/* Right Side - Chat Assistant Preview */}
          <div className="lg:col-span-5">
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden sticky top-24">
              {/* Chat Header */}
              <div className="bg-gradient-to-r from-uri-teal to-uri-green p-5">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center">
                    <i className="ri-customer-service-2-line text-2xl text-uri-teal"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-white text-base">Asistente de Cuidado URI</h4>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-uri-green-light rounded-full animate-pulse"></div>
                      <span className="text-xs text-white/80">En línea - Listo para ayudar</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Chat Messages */}
              <div className="p-6 space-y-4 h-96 overflow-y-auto bg-gray-50">
                {/* Bot Message */}
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-uri-teal/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-robot-line text-uri-teal"></i>
                  </div>
                  <div className="bg-white rounded-2xl rounded-tl-none p-4 shadow-sm max-w-xs">
                    <p className="text-sm text-gray-800">
                      ¡Hola! Soy su Asistente de Cuidado URI. Puedo ayudar a responder preguntas sobre nuestros servicios, programar citas y proporcionar información sobre cuidado residencial y hospitalario. ¿Cómo puedo asistirle hoy?
                    </p>
                  </div>
                </div>

                {/* Quick Reply Suggestions */}
                <div className="flex flex-wrap gap-2 pl-11">
                  <button
                    onClick={() => document.querySelector('#vapi-widget-floating-button')?.dispatchEvent(new Event('click'))}
                    className="px-4 py-2 bg-white border border-uri-teal text-uri-teal text-xs font-medium rounded-full hover:bg-uri-teal/5 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Cuénteme sobre cuidado residencial
                  </button>
                  <button
                    onClick={() => document.querySelector('#vapi-widget-floating-button')?.dispatchEvent(new Event('click'))}
                    className="px-4 py-2 bg-white border border-uri-teal text-uri-teal text-xs font-medium rounded-full hover:bg-uri-teal/5 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Programar una cita
                  </button>
                  <button
                    onClick={() => document.querySelector('#vapi-widget-floating-button')?.dispatchEvent(new Event('click'))}
                    className="px-4 py-2 bg-white border border-uri-teal text-uri-teal text-xs font-medium rounded-full hover:bg-uri-teal/5 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Preguntas sobre seguro
                  </button>
                </div>

                {/* Feature Highlights */}
                <div className="bg-uri-teal/5 rounded-xl p-4 mt-6">
                  <h5 className="font-semibold text-gray-900 text-sm mb-3">Nuestro Asistente Puede Ayudar Con:</h5>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line text-uri-green mt-0.5"></i>
                      <span className="text-xs text-gray-700">Responder preguntas sobre servicios</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line text-uri-green mt-0.5"></i>
                      <span className="text-xs text-gray-700">Programar citas</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line text-uri-green mt-0.5"></i>
                      <span className="text-xs text-gray-700">Información sobre seguro y facturación</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line text-uri-green mt-0.5"></i>
                      <span className="text-xs text-gray-700">Orientación de apoyo de emergencia</span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Chat Input */}
              <div className="p-4 border-t border-gray-200 bg-white">
                <button
                  onClick={() => document.querySelector('#vapi-widget-floating-button')?.dispatchEvent(new Event('click'))}
                  className="w-full flex items-center gap-3 px-4 py-3 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors cursor-pointer"
                >
                  <i className="ri-message-3-line text-gray-600"></i>
                  <span className="text-sm text-gray-600">Haga clic para comenzar a chatear...</span>
                  <i className="ri-send-plane-line text-gray-600 ml-auto"></i>
                </button>
                <p className="text-xs text-gray-500 text-center mt-2">
                  <i className="ri-voice-recognition-line mr-1"></i>
                  Chat de voz también disponible
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
